import { withDependencies, named, optional } from '@wix/thunderbolt-ioc'
import { EditorFeatureConfigSymbol, SiteFeatureConfigSymbol } from '@wix/thunderbolt-symbols'
import { INavigation, NavigationSymbol } from 'feature-navigation'
import { IRoutingLinkUtilsAPI, RoutingLinkUtilsAPISymbol } from 'feature-router'
import { name } from '../symbols'
import {
	EditorLocationWixCodeSdkHandlers,
	LocationWixCodeSdkEditorConfig,
	LocationWixCodeSdkSiteConfig,
} from '../types'
import { IPageTransition, PageTransitionsSymbol } from 'feature-page-transitions'
import _ from 'lodash'
import type { TpaPageLinkData } from '@wix/thunderbolt-becky-types'
import { createLinkUtils } from '@wix/thunderbolt-commons'
import { ILightboxUtils, LightboxUtilsSymbol } from 'feature-lightbox'
import { IMultilingualLinkUtilsAPI, MultilingualLinkUtilsAPISymbol } from 'feature-multilingual'

export const EditorSDKHandlers = withDependencies(
	[
		NavigationSymbol,
		RoutingLinkUtilsAPISymbol,
		named(SiteFeatureConfigSymbol, name),
		named(EditorFeatureConfigSymbol, name),
		optional(PageTransitionsSymbol),
		optional(LightboxUtilsSymbol),
		optional(MultilingualLinkUtilsAPISymbol),
	],
	(
		navigation: INavigation,
		routingLinkUtilsAPI: IRoutingLinkUtilsAPI,
		siteConfig: LocationWixCodeSdkSiteConfig,
		editorConfig: LocationWixCodeSdkEditorConfig,
		pageTransition?: IPageTransition,
		lightboxUtils?: ILightboxUtils,
		multilingualLinkUtilsAPI?: IMultilingualLinkUtilsAPI
	): EditorLocationWixCodeSdkHandlers => {
		return {
			navigateToSection: async ({ compId, sectionId, noTransition, queryParams, state }) => {
				const { routersConfigMap } = siteConfig

				const {
					metaSiteId,
					userFileDomainUrl,
					isMobileView,
					compIdToAppId,
					appIdToAppPagesIds,
					appIdToSectionIdToPageId,
					isPremiumDomain,
					experiments,
				} = editorConfig!

				const linkUtils = createLinkUtils({
					routingInfo: routingLinkUtilsAPI.getLinkUtilsRoutingInfo(),
					metaSiteId,
					userFileDomainUrl,
					routersConfig: routersConfigMap,
					popupPages: lightboxUtils?.getLightboxPages(),
					multilingualInfo: multilingualLinkUtilsAPI?.getMultilingualInfo(),
					isMobileView,
					isPremiumDomain,
					experiments,
				})

				const appId = compIdToAppId[compId]

				const nextAppPages = appIdToAppPagesIds[appId]
				if (!nextAppPages || nextAppPages.length === 0) {
					throw new Error(`Page with app "${appId}" was not found.`)
				}

				const sectionPageId = appIdToSectionIdToPageId[appId][sectionId]

				if (!sectionPageId) {
					throw new Error(`App page with sectionId "${sectionId}" was not found.`)
				}
				const nextPageId = sectionPageId || nextAppPages[0]

				const linkData = {
					type: 'TpaPageLink',
					pageId: nextPageId,
					path: state,
				} as TpaPageLinkData

				const linkUrl = linkUtils.getLinkUrlFromDataItem(linkData)

				const queryParamsUrl = _.isEmpty(queryParams)
					? ''
					: `?appSectionParams=${encodeURIComponent(JSON.stringify(queryParams))}`
				const url = `${linkUrl}${queryParamsUrl}`

				const linkProps = linkUtils.getLinkProps(url)

				if (noTransition && pageTransition) {
					pageTransition.disableNextTransition()
				}

				await navigation.navigateTo(linkProps)
			},
		}
	}
)
